import { useState } from 'react';
import { LoadFailed } from '../components/QueryState';
import { createFileRoute, Link, useNavigate } from '@tanstack/react-router';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { canEditPayeeAccounts } from '@promanage/core';
import { Button, Chip, Field, Input, Panel, PanelHeader, Select } from '@promanage/ui';
import { TopBar } from '../components/TopBar';
import { DialogActions, FormDialog } from '../components/FormDialog';
import { Checklist, ChecklistRow } from '../features/settings/Checklist';
import { MailboxPanel } from '../features/settings/MailboxPanel';
import { RepliesPanel } from '../features/settings/RepliesPanel';
import { SettingsNav } from '../features/settings/SettingsNav';
import { useOrgSettings, type OrgSettings } from '../features/settings/org';
import { useSession } from '../features/auth/session';
import { EmailStopsPanel } from '../features/correspondence/EmailStops';
import { needsStepUp, trpc } from '../lib/trpc';

export const Route = createFileRoute('/settings/delivery')({
  component: DeliveryPage,
});

/**
 * Whether — and when — owner statements leave the building on their own.
 *
 * ── THE PAGE'S JOB IS TO MAKE THE DECISION LEGIBLE BEFORE IT IS MADE ──────────────────────────
 *
 * Turning this on means documents about other people's money start being emailed on a timer, to
 * addresses this agency typed into contact records, to owners who may not be expecting them. That
 * is not a preference and it should not read like one. So the screen shows the current schedule,
 * then what an owner will actually receive — a line each rather than a paragraph each — before
 * the switch, which sits behind "Change schedule".
 *
 * OFF IS THE DEFAULT AND OFF IS NOT BROKEN. An agency that never turns this on still produces
 * every statement and still files them; it just presses send itself. The page says so, so nobody
 * enables it believing it is the only way to get a statement out.
 */
function DeliveryPage() {
  const org = useOrgSettings();
  const session = useSession();
  // The same authority that changes where money is SENT changes whether documents about it are
  // sent unattended — see the note on the router.
  const mayChange = canEditPayeeAccounts(session?.roles ?? []);
  const [editing, setEditing] = useState(false);

  return (
    <div className="mx-auto max-w-[var(--content-max)] pb-4">
      <TopBar
        title="Delivery"
        subtitle={org.data ? org.data.name : 'How owner statements are sent'}
        actions={
          mayChange && org.data ? (
            <Button size="sm" onClick={() => setEditing(true)}>
              Change schedule
            </Button>
          ) : null
        }
      />

      <SettingsNav />

      {org.isError ? (
        <LoadFailed retry={() => void org.refetch()} retrying={org.isFetching} />
      ) : null}

      {org.data ? (
        <div className="grid items-start gap-4 lg:grid-cols-5">
          <div className="flex flex-col gap-4 lg:col-span-3">
            <SchedulePanel settings={org.data} />
            <RepliesPanel settings={org.data} mayChange={mayChange} />
            <MailboxPanel mayChange={mayChange} />
            <EmailStopsPanel />
          </div>
          <div className="lg:col-span-2">
            <ReceivesPanel />
          </div>
        </div>
      ) : null}

      {editing && org.data ? (
        <SetScheduleDialog settings={org.data} onClose={() => setEditing(false)} />
      ) : null}
    </div>
  );
}

function SchedulePanel({ settings }: { settings: OrgSettings }) {
  const delivery = settings.statementDelivery;

  return (
    <Panel className="p-5">
      <PanelHeader
        title="Scheduled statements"
        subtitle="Last month’s statements, emailed to owners"
        className="p-0 pb-4"
        aside={delivery.enabled ? <Chip tone="positive">On</Chip> : <Chip tone="neutral">Off</Chip>}
      />

      <dl className="grid gap-4 rounded-md bg-surface-sunk px-4 py-3 sm:grid-cols-3">
        <Fact label="Sends">
          {delivery.enabled ? (
            `The ${ordinal(delivery.day)} of each month`
          ) : (
            // Off is the default and off is not broken: statements are still produced and filed,
            // and somebody presses send.
            <span className="font-normal text-ink-muted">By hand, from Reports</span>
          )}
        </Fact>
        <Fact label="From">{delivery.senderName ?? settings.name}</Fact>
        <Fact label="Replies to">
          {/* The address a reply actually reaches under the mode chosen in Replies, below. */}
          {settings.replies.address ?? (
            /*
             * SAID AS A GAP, not as a dash. Without it delivery cannot run at all — the refusal is
             * in `setStatementDelivery` and again in the worker — so an empty value here is a
             * thing to go and fix rather than a field somebody left blank.
             */
            <span className="text-danger">Not set — required to send</span>
          )}
        </Fact>
      </dl>
    </Panel>
  );
}

function Fact({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="min-w-0">
      <dt className="text-[0.75rem] text-ink-muted">{label}</dt>
      <dd className="mt-1 truncate text-[0.8125rem] font-semibold">{children}</dd>
    </div>
  );
}

/**
 * WHAT THE OWNER ACTUALLY GETS. The person deciding whether to turn this on is deciding on behalf
 * of forty owners who did not ask, and "an email" is not enough to decide with. Every row is a
 * property of the code rather than a promise:
 *
 * - one message per contact on the owner entity, so trustees never see each other's addresses;
 * - the filed PDF itself, not a second rendering of the month;
 * - a covering note with no tracking pixel, no marketing, no link to a login they do not have;
 * - an empty month still takes its number in the owner's gapless series, but is not sent — an
 *   empty page arriving unannounced reads as a fault;
 * - an owner with no address has theirs filed and listed under Reports → Statement delivery.
 *
 * Mail goes out from an address ProManage controls so providers can verify it without the agency
 * changing its own DNS; the "From" name and reply-to above are what the owner sees.
 */
function ReceivesPanel() {
  return (
    <Panel className="p-5">
      <PanelHeader title="What an owner receives" className="p-0 pb-3" />
      <Checklist>
        <ChecklistRow label="One email per contact">Trustees each get their own</ChecklistRow>
        <ChecklistRow label="The statement PDF">The filed copy</ChecklistRow>
        <ChecklistRow label="A short covering note">Paid out and held</ChecklistRow>
        <ChecklistRow label="Your branding">
          <Link to="/settings/branding" className="text-accent hover:underline">
            Set in Branding
          </Link>
        </ChecklistRow>
        <ChecklistRow yes={false} label="Tracking or marketing">
          Never
        </ChecklistRow>
        <ChecklistRow yes={false} label="Months with no activity">
          Not sent
        </ChecklistRow>
        <ChecklistRow yes={false} label="Owners with no email">
          Listed in Reports
        </ChecklistRow>
      </Checklist>
    </Panel>
  );
}

function SetScheduleDialog({ settings, onClose }: { settings: OrgSettings; onClose: () => void }) {
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const delivery = settings.statementDelivery;

  const [enabled, setEnabled] = useState(delivery.enabled);
  const [day, setDay] = useState(delivery.day);
  const [senderName, setSenderName] = useState(delivery.senderName ?? '');
  const [replyTo, setReplyTo] = useState(delivery.replyTo ?? '');

  const save = useMutation(
    trpc.delivery.setSchedule.mutationOptions({
      meta: { errorTitle: 'Could not change the delivery schedule' },
      onSuccess() {
        void queryClient.invalidateQueries({ queryKey: trpc.org.settings.queryKey() });
        onClose();
      },
      onError(error) {
        // Fifteen minutes since the last code. Not something to explain in a red box.
        if (needsStepUp(error)) void navigate({ to: '/mfa' });
      },
    }),
  );

  const submit = (event: React.FormEvent) => {
    event.preventDefault();
    save.mutate({
      enabled,
      day,
      senderName: senderName.trim() === '' ? null : senderName.trim(),
      replyTo: replyTo.trim() === '' ? null : replyTo.trim(),
    });
  };

  return (
    <FormDialog title="Scheduled statements" onClose={onClose} onSubmit={submit}>
      <label className="flex items-start gap-2 text-[0.8125rem]">
        <input
          type="checkbox"
          checked={enabled}
          onChange={(e) => setEnabled(e.target.checked)}
          className="mt-0.5 size-4 shrink-0 accent-[var(--color-accent)]"
        />
        <span>
          Email last month’s statements automatically
          <span className="block text-[0.75rem] text-ink-muted">
            To every owner this agency manages
          </span>
        </span>
      </label>

      {enabled ? (
        <Field label="Day of the month" required hint="28th is the latest every month has">
          {(props) => (
            <Select {...props} value={String(day)} onChange={(e) => setDay(Number(e.target.value))}>
              {Array.from({ length: 28 }, (_, index) => index + 1).map((value) => (
                <option key={value} value={value}>
                  {ordinal(value)}
                </option>
              ))}
            </Select>
          )}
        </Field>
      ) : null}

      <Field label="From" hint="The name owners know you by">
        {(props) => (
          <Input
            {...props}
            value={senderName}
            onChange={(e) => setSenderName(e.target.value)}
            placeholder={settings.name}
            maxLength={120}
          />
        )}
      </Field>

      {/*
       * ONLY WHERE THE AGENCY ANSWERS AT ITS OWN ADDRESS. With replies arriving in ProManage there
       * is no address to type — the Replies panel says where they go — and the one on file is
       * passed through untouched.
       */}
      {settings.replies.mode === 'own_address' ? (
        <Field label="Replies to" required={enabled}>
          {(props) => (
            <Input
              {...props}
              type="email"
              inputMode="email"
              value={replyTo}
              onChange={(e) => setReplyTo(e.target.value)}
              placeholder="accounts@youragency.co.nz"
            />
          )}
        </Field>
      ) : null}

      {/*
       * SAID AT THE MOMENT OF DECISION. Turning this on sends nothing today — the next run does —
       * and the other reading ("this will email eleven months of statements right now") is the
       * reason somebody would hesitate over the right choice. The change is audit-logged.
       */}
      {enabled && !delivery.enabled ? (
        <p className="text-[0.75rem] text-ink-muted">
          Nothing is sent today — the first run is on the {ordinal(day)}.
        </p>
      ) : null}

      <DialogActions pending={save.isPending} label="Save" onClose={onClose} />
    </FormDialog>
  );
}

/** "5th", "1st", "22nd" — a day of the month as a person would say it. */
function ordinal(day: number): string {
  // 11th, 12th and 13th are the exceptions the naive rule gets wrong, and a settings screen that
  // offers "the 11st" reads as unfinished.
  const teens = day % 100;
  if (teens >= 11 && teens <= 13) return `${String(day)}th`;
  const suffix = { 1: 'st', 2: 'nd', 3: 'rd' }[day % 10] ?? 'th';
  return `${String(day)}${suffix}`;
}
